import { Button, Empty, Pagination, Spin, Tabs, Modal, DatePicker } from "antd";
import { ChevronLeft, ChevronRight } from "lucide-react";
import dayjs from "dayjs";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import React, { useMemo, useState } from "react";
import { useGetServiceTickets } from "~/services/service-ticket";
import { SortOrder, ServiceType } from "~/utils/constants";
import { ContainerInfoCard } from "~/components/common/container-info";

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

export const TodaySchedule: React.FC = () => {
  const [page, setPage] = useState(1);
  const [activeTab, setActiveTab] = useState("all");
  const [viewMode, setViewMode] = useState<"schedule" | "day" | "2day" | "4day" | "week">("schedule");
  const [selectedService, setSelectedService] = useState<any>(null);
  const [selectedDate, setSelectedDate] = useState(dayjs());
  
  // Time slots for calendar grid (7am to 7pm)
  const timeSlots = useMemo(() => {
    const slots = [];
    for (let hour = 7; hour <= 19; hour++) {
      const time = dayjs().hour(hour).minute(0).second(0);
      slots.push({
        hour,
        label: time.format("h A"),
        fullLabel: time.format("h:mm A")
      });
    }
    return slots;
  }, []);

  // FIXED: Always use the same API call regardless of view mode
  // This ensures schedule view and calendar views see the exact same data
  const apiDateRange = useMemo(() => {
    // For calendar views, we need a wider range to show multiple days
    // But we'll fetch a reasonable range and filter on frontend
    if (viewMode === "schedule" || viewMode === "day") {
      return {
        startDate: selectedDate.startOf("day").toISOString(),
        endDate: selectedDate.endOf("day").toISOString(),
      };
    } else if (viewMode === "2day") {
      return {
        startDate: selectedDate.startOf("day").toISOString(),
        endDate: selectedDate.add(1, "day").endOf("day").toISOString(),
      };
    } else if (viewMode === "4day") {
      return {
        startDate: selectedDate.startOf("day").toISOString(),
        endDate: selectedDate.add(3, "day").endOf("day").toISOString(),
      };
    } else { // week
      return {
        startDate: selectedDate.startOf("week").toISOString(),
        endDate: selectedDate.endOf("week").toISOString(),
      };
    }
  }, [selectedDate, viewMode]);

  const { data, isLoading } = useGetServiceTickets({
    page: 1,
    pageSize: 100,
    startDate: apiDateRange.startDate,
    endDate: apiDateRange.endDate,
    sortByCreatedAt: SortOrder.DESC,
  });

  // Generate days array based on view mode
  const viewDays = useMemo(() => {
    const days = [];
    
    switch (viewMode) {
      case "day":
        days.push(selectedDate);
        break;
      case "2day":
        for (let i = 0; i < 2; i++) {
          days.push(selectedDate.add(i, "day"));
        }
        break;
      case "4day":
        for (let i = 0; i < 4; i++) {
          days.push(selectedDate.add(i, "day"));
        }
        break;
      case "week":
        const startOfWeek = selectedDate.startOf("week");
        for (let i = 0; i < 7; i++) {
          days.push(startOfWeek.add(i, "day"));
        }
        break;
      default:
        days.push(selectedDate);
        break;
    }
    
    return days;
  }, [viewMode, selectedDate]);

  // STEP 1: Filter services based on active tab (from raw API data)
  const tabFilteredServices = useMemo(() => {
    if (!data?.items) return [];
    
    console.log("=== DEBUGGING INFO ===");
    console.log("View Mode:", viewMode);
    console.log("Selected Date:", selectedDate.format("YYYY-MM-DD"));
    console.log("API Date Range:", apiDateRange);
    console.log("Raw API Data:", data.items.length, "services");
    
    // DEBUG: Show the structure of the first few services to understand date fields
    console.log("=== SERVICE STRUCTURE DEBUG ===");
    data.items.slice(0, 3).forEach((service, index) => {
      console.log(`Service ${index + 1}:`, {
        id: service.id,
        serviceType: service.serviceType,
        startDatetime: service.startDatetime,
        serviceDatetime: service.serviceDatetime,
        'booking?.startDatetime': service.booking?.startDatetime,
        createdAt: service.createdAt,
        client: service.client?.companyName,
        allDateFields: Object.keys(service).filter(key => 
          key.toLowerCase().includes('date') || key.toLowerCase().includes('time')
        )
      });
    });
    
    let filtered;
    switch (activeTab) {
      case "transport":
        filtered = data.items.filter(
          (service) =>
            service.serviceType === "COLLECTION" ||
            service.serviceType === "DELIVERY" ||
            service.serviceType === ServiceType.COLLECTION ||
            service.serviceType === ServiceType.DELIVERY
        );
        break;
      case "site":
        filtered = data.items.filter(
          (service) => 
            service.serviceType === "ON_SITE_SERVICE" ||
            service.serviceType === "SITE_SERVICE" ||
            service.serviceType === ServiceType.SITE_SERVICE
        );
        break;
      case "warehouse":
        filtered = data.items.filter(
          (service) =>
            service.serviceType === "WAREHOUSE_SERVICE" ||
            service.serviceType === "FABRICATION_SERVICE" ||
            service.serviceType === ServiceType.WAREHOUSE_SERVICE ||
            service.serviceType === ServiceType.FABRICATION_SERVICE
        );
        break;
      case "all":
      default:
        filtered = data.items;
        break;
    }
    
    console.log("Tab filtered services:", filtered.length, "services for tab:", activeTab);
    if (activeTab !== "all") {
      console.log("Services in this tab:");
      filtered.forEach(service => {
        console.log(`  - ${service.id} (${service.serviceType}) - ${service.client?.companyName}`);
      });
    }
    
    // DEBUG: Show service types breakdown
    const typeBreakdown = {};
    data.items.forEach(service => {
      typeBreakdown[service.serviceType] = (typeBreakdown[service.serviceType] || 0) + 1;
    });
    console.log("Service types breakdown:");
    Object.entries(typeBreakdown).forEach(([type, count]) => {
      console.log(`  ${type}: ${count}`);
    });
    
    return filtered;
  }, [data?.items, activeTab, viewMode, selectedDate, apiDateRange]);

  // STEP 2: Filter services to only include ones that fall within our view range
  // This fixes the API bug where it returns services outside the requested range
  const filteredServices = useMemo(() => {
    const startOfRange = viewDays[0];
    const endOfRange = viewDays[viewDays.length - 1];
    
    console.log("=== DATE FILTERING DEBUG ===");
    console.log("Range:", startOfRange.format("YYYY-MM-DD"), "to", endOfRange.format("YYYY-MM-DD"));
    
    return tabFilteredServices.filter(service => {
      // Debug ALL possible date fields for this service
      console.log(`\nService ${service.id} (${service.serviceType}) date fields:`);
      console.log("  startDatetime:", service.startDatetime);
      console.log("  serviceDatetime:", service.serviceDatetime);
      console.log("  booking?.startDatetime:", service.booking?.startDatetime);
      console.log("  createdAt:", service.createdAt);
      console.log("  updatedAt:", service.updatedAt);
      console.log("  timeIn:", service.timeIn);
      console.log("  timeOut:", service.timeOut);
      
      // Check if there are any other date-related fields
      const allFields = Object.keys(service);
      const dateFields = allFields.filter(key => 
        key.toLowerCase().includes('date') || 
        key.toLowerCase().includes('time') ||
        key.toLowerCase().includes('schedule')
      );
      console.log("  All date-related fields:", dateFields);
      dateFields.forEach(field => {
        if (!['startDatetime', 'serviceDatetime', 'createdAt', 'updatedAt', 'timeIn', 'timeOut'].includes(field)) {
          console.log(`  ${field}:`, service[field]);
        }
      });
      
      // Use the EXACT same date fields as the working services page
      // serviceDatetime = scheduled date, startDatetime = start time
      const scheduledDate = service.serviceDatetime;  // This should be the actual scheduled date
      const serviceDateTime = scheduledDate || service.startDatetime; // Fallback to startDatetime if needed
      
      if (!serviceDateTime) {
        console.log(`  ❌ Service ${service.id} has no usable date field`);
        console.log(`    serviceDatetime: ${service.serviceDatetime}`);
        console.log(`    startDatetime: ${service.startDatetime}`);
        return false;
      }
      
      const serviceDate = dayjs(serviceDateTime);
      const isInRange = serviceDate.isSameOrAfter(startOfRange, 'day') && 
                       serviceDate.isSameOrBefore(endOfRange, 'day');
      
      console.log(`  📅 Using date field: ${scheduledDate ? 'serviceDatetime' : 'startDatetime'}`);
      console.log(`  📅 Date value: ${serviceDateTime} -> ${serviceDate.format("YYYY-MM-DD")}`);
      console.log(`  ${isInRange ? '✅ KEEPING' : '❌ FILTERING OUT'} - ${isInRange ? 'in range' : 'outside range'}`);
      
      return isInRange;
    });
  }, [tabFilteredServices, viewDays]);

  // STEP 3: Group the filtered services by date and time slot
  const servicesByDateAndTime = useMemo(() => {
    const grouped = new Map<string, Map<number, any[]>>();
    const overflowServices = new Map<string, { early: any[], late: any[] }>();
    
    filteredServices.forEach((service) => {
      // Use serviceDatetime for date (filtering), startDatetime for actual time
      const scheduledDate = service.serviceDatetime || service.startDatetime;
      const actualTime = service.startDatetime; // This should have the correct time
      
      if (scheduledDate && actualTime) {
        const serviceDate = dayjs(scheduledDate);
        const timeWithHour = dayjs(actualTime);
        const dateKey = serviceDate.format("YYYY-MM-DD");
        const hour = timeWithHour.hour(); // Extract hour from startDatetime for correct time
        
        console.log(`Service ${service.id}: date=${dateKey}, startDatetime=${actualTime}, hour=${hour}`);
        
        if (!grouped.has(dateKey)) {
          grouped.set(dateKey, new Map());
        }
        
        if (!overflowServices.has(dateKey)) {
          overflowServices.set(dateKey, { early: [], late: [] });
        }
        
        // Check if service is within main time slots (7am-7pm)
        if (hour < 7) {
          overflowServices.get(dateKey)!.early.push(service);
        } else if (hour >= 19) {
          overflowServices.get(dateKey)!.late.push(service);
        } else {
          // Service is within main time slots
          const dayMap = grouped.get(dateKey)!;
          if (!dayMap.has(hour)) {
            dayMap.set(hour, []);
          }
          dayMap.get(hour)!.push(service);
        }
      }
    });

    console.log("Final services in view range:", filteredServices.length);
    console.log("Services grouped by date and time:");
    grouped.forEach((timeMap, date) => {
      console.log(`  ${date}:`);
      timeMap.forEach((services, hour) => {
        console.log(`    ${hour}:00 - ${services.length} services`);
      });
    });

    return { grouped, overflowServices };
  }, [filteredServices]);

  // Calculate tab counts based on services actually in the view range
  const tabCounts = useMemo(() => {
    if (!tabFilteredServices) return { all: 0, transport: 0, site: 0, warehouse: 0 };
    
    // Filter the tab-filtered services by date range
    const startOfRange = viewDays[0];
    const endOfRange = viewDays[viewDays.length - 1];
    
    const servicesInRange = tabFilteredServices.filter(service => {
      // Use same logic: serviceDatetime for scheduled date, startDatetime as fallback
      const serviceDateTime = service.serviceDatetime || service.startDatetime;
      if (!serviceDateTime) return false;
      
      const serviceDate = dayjs(serviceDateTime);
      return serviceDate.isSameOrAfter(startOfRange, 'day') && 
             serviceDate.isSameOrBefore(endOfRange, 'day');
    });
    
    const all = servicesInRange.length;
    const transport = servicesInRange.filter(
      (service) =>
        service.serviceType === "COLLECTION" ||
        service.serviceType === "DELIVERY" ||
        service.serviceType === ServiceType.COLLECTION ||
        service.serviceType === ServiceType.DELIVERY
    ).length;
    const site = servicesInRange.filter(
      (service) => 
        service.serviceType === "ON_SITE_SERVICE" ||
        service.serviceType === "SITE_SERVICE" ||
        service.serviceType === ServiceType.SITE_SERVICE
    ).length;
    const warehouse = servicesInRange.filter(
      (service) =>
        service.serviceType === "WAREHOUSE_SERVICE" ||
        service.serviceType === "FABRICATION_SERVICE" ||
        service.serviceType === ServiceType.WAREHOUSE_SERVICE ||
        service.serviceType === ServiceType.FABRICATION_SERVICE
    ).length;

    console.log("Tab counts calculation:");
    console.log(`  All: ${all}`);
    console.log(`  Transport: ${transport}`);
    console.log(`  Site: ${site}`);
    console.log(`  Warehouse: ${warehouse}`);

    return { all, transport, site, warehouse };
  }, [tabFilteredServices, viewDays]);

  // Paginate filtered results for schedule view only
  const paginatedServices = useMemo(() => {
    if (viewMode !== "schedule") return [];

    // Define service type priority (lower number = higher priority)
    const getServiceTypePriority = (serviceType: string) => {
      switch (serviceType) {
        case ServiceType.COLLECTION:
        case ServiceType.DELIVERY:
          return 1; // Vessel services first
        case ServiceType.ON_SITE_SERVICE:
          return 2; // On-site second
        case ServiceType.WAREHOUSE_SERVICE:
          return 3; // Warehouse third
        case ServiceType.FABRICATION_SERVICE:
          return 4; // Fabrication fourth
        default:
          return 5;
      }
    };

    // Sort by service type priority, then by vessel, then by time
    const sorted = [...filteredServices].sort((a, b) => {
      // First: Sort by service type priority
      const priorityA = getServiceTypePriority(a.serviceType);
      const priorityB = getServiceTypePriority(b.serviceType);

      if (priorityA !== priorityB) {
        return priorityA - priorityB;
      }

      // Second: For vessel services (collection/delivery), group by vessel
      if ((a.serviceType === ServiceType.COLLECTION || a.serviceType === ServiceType.DELIVERY) &&
          (b.serviceType === ServiceType.COLLECTION || b.serviceType === ServiceType.DELIVERY)) {
        const vesselA = a.vessel?.name || '';
        const vesselB = b.vessel?.name || '';

        if (vesselA !== vesselB) {
          return vesselA.localeCompare(vesselB);
        }
      }

      // Third: Sort by start time within the same group
      const timeA = dayjs(a.startDatetime);
      const timeB = dayjs(b.startDatetime);

      // Handle invalid dates
      if (!timeA.isValid()) return 1;
      if (!timeB.isValid()) return -1;

      return timeA.isBefore(timeB) ? -1 : timeA.isAfter(timeB) ? 1 : 0;
    });

    const startIndex = (page - 1) * 10;
    const endIndex = startIndex + 10;
    return sorted.slice(startIndex, endIndex);
  }, [filteredServices, page, viewMode]);

  // Service card component for grid - using ContainerInfoCard
  const GridServiceCard = ({ service, compact = false }: { service: any; compact?: boolean }) => {
    const duration = service.duration || 1;
    
    return (
      <div 
        className="mb-1 cursor-pointer"
        style={{ 
          minHeight: `${Math.max(duration * 60 - 8, 40)}px`, // Account for duration
        }}
        onClick={(e) => {
          e.stopPropagation();
          setSelectedService(service);
        }}
      >
        <ContainerInfoCard
          serviceTicket={service}
          variant="compact"
          showHeader={false}
          className="border border-gray-200 shadow-sm hover:shadow-md transition-shadow text-xs"
        />
      </div>
    );
  };

  // Overflow service component (for early/late services) - using ContainerInfoCard
  const OverflowServiceCard = ({ service }: { service: any }) => {
    return (
      <div 
        className="mb-1 cursor-pointer"
        onClick={(e) => {
          e.stopPropagation();
          setSelectedService(service);
        }}
      >
        <ContainerInfoCard
          serviceTicket={service}
          variant="compact"
          showHeader={false}
          className="border border-gray-300 bg-gray-100 hover:bg-gray-200 transition-colors text-xs"
        />
      </div>
    );
  };

  // Navigation functions
  const navigatePrevious = () => {
    switch (viewMode) {
      case "schedule":
      case "day":
        setSelectedDate(selectedDate.subtract(1, "day"));
        break;
      case "2day":
        setSelectedDate(selectedDate.subtract(2, "day"));
        break;
      case "4day":
        setSelectedDate(selectedDate.subtract(4, "day"));
        break;
      case "week":
        setSelectedDate(selectedDate.subtract(1, "week"));
        break;
    }
    setPage(1);
  };

  const navigateNext = () => {
    switch (viewMode) {
      case "schedule":
      case "day":
        setSelectedDate(selectedDate.add(1, "day"));
        break;
      case "2day":
        setSelectedDate(selectedDate.add(2, "day"));
        break;
      case "4day":
        setSelectedDate(selectedDate.add(4, "day"));
        break;
      case "week":
        setSelectedDate(selectedDate.add(1, "week"));
        break;
    }
    setPage(1);
  };

  const handleDateChange = (date: dayjs.Dayjs | null) => {
    if (date) {
      setSelectedDate(date);
      setPage(1);
    }
  };

  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
    setPage(1);
  };

  const tabItems = [
    { key: "all", label: `All (${tabCounts.all})` },
    { key: "transport", label: `Transport (${tabCounts.transport})` },
    { key: "site", label: `Site (${tabCounts.site})` },
    { key: "warehouse", label: `Warehouse (${tabCounts.warehouse})` },
  ];

  return (
    <div className="bg-white rounded-lg border p-6">
      {/* Header with Navigation */}
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center space-x-4">
          <h2 className="text-xl font-medium">
            {viewMode === "schedule" ? "Schedule" : 
             viewMode === "day" ? "Day View" :
             viewMode === "2day" ? "2-Day View" :
             viewMode === "4day" ? "4-Day View" :
             "Week View"}
          </h2>
          
          {/* Date Navigation */}
          <div className="flex items-center space-x-2">
            <Button 
              type="text" 
              icon={<ChevronLeft size={16} />}
              onClick={navigatePrevious}
              className="hover:bg-gray-100"
            />
            
            <DatePicker
              value={selectedDate}
              onChange={handleDateChange}
              format="MMM DD, YYYY"
              className="border-0 text-center cursor-pointer hover:bg-gray-50"
              suffixIcon={null}
              allowClear={false}
            />
            
            <Button 
              type="text" 
              icon={<ChevronRight size={16} />}
              onClick={navigateNext}
              className="hover:bg-gray-100"
            />
          </div>
        </div>

        {/* View Mode Buttons */}
        <div className="flex space-x-2">
          {[
            { key: "schedule", label: "Schedule", color: "gray" },
            { key: "day", label: "Day", color: "blue" },
            { key: "2day", label: "2 Days", color: "blue" },
            { key: "4day", label: "4 Days", color: "blue" },
            { key: "week", label: "Week", color: "blue" }
          ].map(({ key, label, color }) => (
            <button 
              key={key}
              className={`px-3 py-1.5 rounded-md text-sm transition-colors ${
                viewMode === key 
                  ? color === "gray" 
                    ? "bg-gray-900 text-white" 
                    : "bg-blue-600 text-white"
                  : color === "gray"
                    ? "text-gray-600 hover:bg-gray-50 border border-gray-200"
                    : "text-blue-600 hover:bg-blue-50 border border-blue-200"
              }`}
              onClick={() => setViewMode(key as any)}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Service Type Tabs */}
      <div className="mb-6">
        <Tabs
          activeKey={activeTab}
          onChange={handleTabChange}
          items={tabItems}
          className="service-tabs"
        />
      </div>

      {/* Content based on view mode */}
      <Spin spinning={isLoading}>
        {viewMode === "schedule" ? (
          // SCHEDULE VIEW - List view with pagination
          <div className="space-y-4">
            {paginatedServices.length > 0 ? (
              <>
                {paginatedServices.map((service) => (
                  <ContainerInfoCard
                    key={service.id}
                    serviceTicket={service}
                    variant="simpleschedule"
                    showHeader={false}
                    className="hover:shadow-md transition-shadow"
                  />
                ))}
                {filteredServices.length > 10 && (
                  <div className="p-4 border-t bg-gray-50 flex justify-center">
                    <Pagination
                      total={filteredServices.length}
                      current={page}
                      pageSize={10}
                      onChange={setPage}
                      showSizeChanger={false}
                    />
                  </div>
                )}
              </>
            ) : (
              <Empty description={`No ${activeTab === "all" ? "" : activeTab} services scheduled for ${selectedDate.format("MM/DD/YYYY")}`} />
            )}
          </div>
        ) : (
          // CALENDAR GRID VIEWS - Google Calendar style
          <div className="overflow-auto" style={{ maxHeight: '800px' }}>
            <div className="min-w-[800px]">
              {/* Day headers */}
              <div className="grid border-b border-gray-200 bg-gray-50" style={{ gridTemplateColumns: `80px repeat(${viewDays.length}, 1fr)` }}>
                <div className="p-2"></div> {/* Empty cell for time column */}
                {viewDays.map((day, index) => {
                  const isToday = day.isSame(dayjs(), 'day');
                  const dayKey = day.format("YYYY-MM-DD");
                  const dayTimeMap = servicesByDateAndTime.grouped.get(dayKey) || new Map();
                  const overflowData = servicesByDateAndTime.overflowServices.get(dayKey) || { early: [], late: [] };
                  const totalServices = Array.from(dayTimeMap.values()).reduce((sum, services) => sum + services.length, 0) + 
                                      overflowData.early.length + overflowData.late.length;
                  
                  return (
                    <div key={index} className={`p-3 text-center border-l border-gray-200 ${isToday ? 'bg-blue-50' : ''}`}>
                      <div className="text-sm font-semibold text-gray-800">
                        {day.format("ddd")}
                      </div>
                      <div className={`text-lg font-bold ${isToday ? 'text-blue-600' : 'text-gray-900'}`}>
                        {day.format("MMM D")}
                      </div>
                      <div className="text-xs text-gray-600 mt-1">
                        {totalServices} service{totalServices !== 1 ? 's' : ''}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Early services (before 7am) - Abbreviated */}
              <div className="grid border-b border-gray-200 bg-yellow-50" style={{ gridTemplateColumns: `80px repeat(${viewDays.length}, 1fr)` }}>
                <div className="p-2 text-xs font-medium text-gray-600 border-r border-gray-200 bg-yellow-100">
                  Before 7AM
                </div>
                {viewDays.map((day, dayIndex) => {
                  const dayKey = day.format("YYYY-MM-DD");
                  const overflowData = servicesByDateAndTime.overflowServices.get(dayKey) || { early: [], late: [] };
                  
                  return (
                    <div key={dayIndex} className="p-1 border-l border-gray-200 min-h-[40px]">
                      {overflowData.early.map(service => (
                        <OverflowServiceCard key={service.id} service={service} />
                      ))}
                    </div>
                  );
                })}
              </div>

              {/* Main time grid (7am-7pm) */}
              {timeSlots.map((timeSlot, timeIndex) => (
                <div 
                  key={timeSlot.hour} 
                  className="grid border-b border-gray-100" 
                  style={{ 
                    gridTemplateColumns: `80px repeat(${viewDays.length}, 1fr)`,
                    minHeight: '60px'
                  }}
                >
                  {/* Time label */}
                  <div className="p-2 text-sm font-medium text-gray-600 border-r border-gray-200 bg-gray-50 flex items-start">
                    {timeSlot.label}
                  </div>
                  
                  {/* Services for each day at this time */}
                  {viewDays.map((day, dayIndex) => {
                    const dayKey = day.format("YYYY-MM-DD");
                    const dayTimeMap = servicesByDateAndTime.grouped.get(dayKey) || new Map();
                    const servicesAtTime = dayTimeMap.get(timeSlot.hour) || [];
                    
                    return (
                      <div key={dayIndex} className="p-1 border-l border-gray-200 relative">
                        {servicesAtTime.map(service => (
                          <GridServiceCard 
                            key={service.id} 
                            service={service} 
                            compact={viewDays.length > 4} 
                          />
                        ))}
                      </div>
                    );
                  })}
                </div>
              ))}

              {/* Late services (after 7pm) - Abbreviated */}
              <div className="grid border-b border-gray-200 bg-purple-50" style={{ gridTemplateColumns: `80px repeat(${viewDays.length}, 1fr)` }}>
                <div className="p-2 text-xs font-medium text-gray-600 border-r border-gray-200 bg-purple-100">
                  After 7PM
                </div>
                {viewDays.map((day, dayIndex) => {
                  const dayKey = day.format("YYYY-MM-DD");
                  const overflowData = servicesByDateAndTime.overflowServices.get(dayKey) || { early: [], late: [] };
                  
                  return (
                    <div key={dayIndex} className="p-1 border-l border-gray-200 min-h-[40px]">
                      {overflowData.late.map(service => (
                        <OverflowServiceCard key={service.id} service={service} />
                      ))}
                    </div>
                  );
                })}
              </div>

              {/* Show message if no services in the entire period */}
              {filteredServices.length === 0 && (
                <div className="p-8 text-center">
                  <Empty description={`No ${activeTab === "all" ? "" : activeTab} services scheduled for this period`} />
                </div>
              )}
            </div>
          </div>
        )}
      </Spin>

      {/* Service Detail Modal */}
      <Modal
        title="Service Details"
        open={!!selectedService}
        onCancel={() => setSelectedService(null)}
        footer={null}
        width={800}
      >
        {selectedService && (
          <div className="cursor-pointer" onClick={() => {
            const bookingId = selectedService.jobTicketId || selectedService.jobTicket?.id;
            if (bookingId) {
              window.location.href = `/bookings/${bookingId}?tab=services&serviceId=${selectedService.id}`;
            }
          }}>
            <ContainerInfoCard
              serviceTicket={selectedService}
              variant="detailed"
              showHeader={true}
              className="border-0"
            />
          </div>
        )}
      </Modal>
    </div>
  );
};